import Axios from '../Config/Axios'

const BASE = '/prod/user/practitioners'

/**
 * GET /user/practitioners
 *
 * Returns all practitioner login records (DynamoDB) that belong to the
 * authenticated user.
 *
 * Response shape:
 * {
 *   success: true,
 *   data: {
 *     items: Array<{
 *       sub: string
 *       username: string
 *       practitionerName: string
 *       status: "ACTIVE" | "PENDING" | string
 *       dashboardId: string
 *       currency: { currencySymbol: string, currencyLabel: string }
 *     }>
 *   }
 * }
 */
export const getPractitioners = () =>
  Axios({ method: 'GET', url: BASE })

/**
 * GET /user/practitioners/names
 *
 * Returns practitioner names pulled directly from the practice management
 * system (PMS) database — used to populate the "Select practitioner" dropdown.
 *
 * Response shape:
 * {
 *   success: true,
 *   data: Array<{ id: string, first_name: string, last_name: string }>
 * }
 */
export const getPractitionerNames = () =>
  Axios({ method: 'GET', url: `${BASE}/names` })

/**
 * GET /user/practitioners/:sub
 *
 * Fetches a single practitioner record by their Cognito sub (UUID).
 *
 * @param {string} sub - The practitioner's Cognito sub UUID.
 *
 * Response shape:
 * {
 *   success: true,
 *   data: {
 *     sub: string
 *     username: string
 *     practitionerName: string
 *     status: string
 *     dashboardId: string
 *     templateId: string
 *     analysisId: string
 *     dataSetIds: string[]
 *     dataSourceId: string
 *     currency: { currencySymbol: string, currencyLabel: string }
 *     callerSub: string
 *     callerUsername: string
 *     PK: string
 *     SK: string
 *   }
 * }
 *
 * Error 400 – NotFound: { success: false, error: { name: "NotFound", message: string } }
 */
export const getPractitionerBySub = (sub) =>
  Axios({ method: 'GET', url: `${BASE}/${sub}` })

/**
 * POST /user/practitioners
 *
 * Creates a new practitioner Cognito user and triggers the async setup flow
 * (QuickSight dashboard creation + welcome email).
 *
 * @param {{ practitionerName: string, username: string, email: string }} payload
 *
 * Response shape:
 * {
 *   success: true,
 *   data: { sub: string, username: string, practitionerName: string, templateId: string }
 * }
 *
 * Error 400 – EmailAlreadyInUse: { success: false, error: { name: "EmailAlreadyInUse", message: string } }
 * Error 400 – UserAlreadyExists: { success: false, error: { name: "UserAlreadyExists",  message: string } }
 */
export const createPractitioner = ({ practitionerName, username, email }) =>
  Axios({
    method: 'POST',
    url: BASE,
    data: { practitionerName, username, email },
  })

/**
 * DELETE /user/practitioners/:sub
 *
 * Disables the practitioner's Cognito user and removes their DynamoDB record.
 *
 * @param {string} sub - The practitioner's Cognito sub UUID.
 *
 * Response shape:
 * { message: "Practitioner <sub> deleted successfully" }
 *
 * Error 400 – Missing sub: { error: "InvalidRequest", message: "Missing path parameter: sub" }
 */
export const deletePractitioner = (sub) =>
  Axios({ method: 'DELETE', url: `${BASE}/${sub}` })
